import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Percent, Tag, Recycle, BadgeCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import CategoryGrid from "../components/CategoryGrid";
import BrandsBar from "../components/BrandsBar";
import ProductCard from "../components/ProductCard";
import BusinessCard from "../components/BusinessCard";
import { PRODUCTS, STORE_INFO, NEWS, SERVICES } from "../mock";
import { useAllProducts } from "../hooks/useProducts";
import * as LucideIcons from "lucide-react";

const Home = () => {
  const allProducts = useAllProducts();
  const produitsNeufs = allProducts.filter((p) => p.condition === "Neuf");
  const occasions = allProducts.filter((p) => p.condition === "Occasion");
  const occasionsFrigo = occasions.filter((p) => p.category === "refrigerateur").slice(0, 4);
  const occasionsLL = occasions.filter((p) => p.category === "lave-linge").slice(0, 4);
  const occasionsSL = occasions.filter((p) => p.category === "seche-linge").slice(0, 4);
  const occasionsCuisson = occasions.filter((p) => p.category === "cuisson").slice(0, 4);
  const occasionsAutres = occasions.filter((p) => !["refrigerateur","lave-linge","seche-linge","cuisson"].includes(p.category)).slice(0, 4);
  const neufsFrigo = produitsNeufs.filter((p) => p.category === "refrigerateur").slice(0, 4);
  const neufsLL = produitsNeufs.filter((p) => p.category === "lave-linge").slice(0, 4);
  const neufsSL = produitsNeufs.filter((p) => p.category === "seche-linge").slice(0, 4);
  const neufsAutres = produitsNeufs.filter((p) => !["refrigerateur","lave-linge","seche-linge"].includes(p.category)).slice(0, 8);

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-[#0b2960] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, #e11d24 0, transparent 40%), radial-gradient(circle at 80% 70%, #ffffff 0, transparent 30%)"
        }} />
        <div className="max-w-7xl mx-auto px-4 py-12 md:py-20 relative grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#e11d24] text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider mb-4">
              <Recycle className="h-3.5 w-3.5" /> Spécialiste occasion & neuf · Jusqu'à -70%
            </div>
            <h1 className="text-4xl md:text-6xl font-black leading-tight tracking-tight">
              L'électroménager<br />
              <span className="text-[#ff5a5f]">d'occasion garanti.</span>
            </h1>
            <p className="mt-4 text-lg md:text-xl text-neutral-200 max-w-lg">
              Frigos, lave-linge, fours et TV d'occasion reconditionnés à Marseille. Testés, garantis 4 mois et à prix imbattables.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/occasion">
                <Button className="bg-[#e11d24] hover:bg-[#b8171d] text-white font-bold h-12 px-6 text-base rounded-md">
                  Voir toutes les occasions <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/boutique">
                <Button variant="outline" className="border-2 border-white bg-transparent text-white hover:bg-white hover:text-[#0b2960] font-bold h-12 px-6 text-base rounded-md">
                  Nos produits neufs
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-neutral-200">
              <div className="flex items-center gap-2"><Percent className="h-4 w-4 text-[#ff5a5f]" /> Prix imbattables</div>
              <div className="flex items-center gap-2"><LucideIcons.Truck className="h-4 w-4 text-[#ff5a5f]" /> Livraison Marseille</div>
              <div className="hidden sm:flex items-center gap-2"><LucideIcons.ShieldCheck className="h-4 w-4 text-[#ff5a5f]" /> Garantie</div>
            </div>
          </div>

          <div className="relative hidden md:block">
            <div className="absolute inset-0 bg-[#e11d24] rounded-2xl rotate-3 opacity-30" />
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20">
              <img
                src="https://images.unsplash.com/photo-1556911220-bff31c812dba"
                alt="Cuisine équipée"
                className="w-full h-[420px] object-cover"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white text-[#0b2960] rounded-xl shadow-xl px-4 py-3">
              <div className="text-3xl font-black">-70%</div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500">sur l'occasion</div>
            </div>
          </div>
        </div>
      </section>

      {/* Promo bar */}
      <div className="bg-[#e11d24] text-white py-3">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm md:text-base font-semibold tracking-wide">
          <span className="uppercase mr-2">♻️ Occasion reconditionnée :</span>
          <span>Jusqu'à -70% sur nos frigos, lave-linge, fours et TV · Garantie 4 mois ·</span>
          <a href={`tel:${STORE_INFO.phone.replace(/\s/g, "")}`} className="underline ml-1 hover:text-neutral-200">{STORE_INFO.phone}</a>
        </div>
      </div>

      <CategoryGrid />

      {/* Pourquoi acheter en occasion chez nous */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-widest font-bold text-[#e11d24]">Nos engagements</div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">Pourquoi acheter d'occasion chez EDS ?</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { icon: LucideIcons.Wrench, title: "Reconditionné en atelier", text: "Chaque appareil est testé, nettoyé et révisé par nos techniciens." },
            { icon: LucideIcons.ShieldCheck, title: "Garantie 4 mois", text: "Pièces et main d'œuvre incluses. Extension jusqu'à 12 mois." },
            { icon: LucideIcons.Recycle, title: "Éco-responsable", text: "Prolongez la vie de l'électroménager, réduisez les déchets." },
            { icon: LucideIcons.Percent, title: "Jusqu'à -70%", text: "Les mêmes grandes marques, à une fraction du prix neuf." },
          ].map((s, i) => (
            <div key={i} className="bg-white border border-neutral-200 rounded-xl p-5 hover:shadow-lg hover:border-[#0b2960]/30 transition-all">
              <div className="h-11 w-11 rounded-full bg-[#e11d24] text-white flex items-center justify-center mb-3">
                <s.icon className="h-5 w-5" />
              </div>
              <h3 className="font-bold text-[#0b2960] text-sm">{s.title}</h3>
              <p className="text-xs text-neutral-600 mt-1 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Réfrigérateurs occasion */}
      {occasionsFrigo.length > 0 && (<section className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#e11d24] flex items-center gap-1"><Tag className="h-3.5 w-3.5" /> Occasion garantie</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">Réfrigérateurs d'occasion</h2>
          </div>
          <Link to="/boutique?cat=refrigerateur" className="text-sm font-semibold text-[#e11d24] hover:underline">Tout voir →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {occasionsFrigo.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>)}

      {/* Lave-linge occasion */}
      {occasionsLL.length > 0 && (<section className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#e11d24] flex items-center gap-1"><Tag className="h-3.5 w-3.5" /> Occasion garantie</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">Lave-linge d'occasion</h2>
          </div>
          <Link to="/boutique?cat=lave-linge" className="text-sm font-semibold text-[#e11d24] hover:underline">Tout voir →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {occasionsLL.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>)}

      {/* Sèche-linge occasion */}
      {occasionsSL.length > 0 && (<section className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#e11d24] flex items-center gap-1"><Tag className="h-3.5 w-3.5" /> Occasion garantie</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">Sèche-linge d'occasion</h2>
          </div>
          <Link to="/occasion" className="text-sm font-semibold text-[#e11d24] hover:underline">Tout voir →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {occasionsSL.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>)}

      {/* Cuisson / Fours occasion */}
      {occasionsCuisson.length > 0 && (<section className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#e11d24] flex items-center gap-1"><Tag className="h-3.5 w-3.5" /> Occasion garantie</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">Cuisson & fours d'occasion</h2>
          </div>
          <Link to="/boutique?cat=cuisson" className="text-sm font-semibold text-[#e11d24] hover:underline">Tout voir →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {occasionsCuisson.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>)}

      {/* Autres occasions (TV, petit électro, lave-vaisselle) */}
      {occasionsAutres.length > 0 && (<section className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#e11d24] flex items-center gap-1"><Tag className="h-3.5 w-3.5" /> Occasion garantie</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">TV, hi-fi & petit électroménager</h2>
          </div>
          <Link to="/occasion" className="text-sm font-semibold text-[#e11d24] hover:underline">Tout voir →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {occasionsAutres.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>)}

      {/* Big Banner */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="relative bg-[#0b2960] rounded-2xl overflow-hidden shadow-xl">
          <div className="absolute inset-y-0 right-0 w-1/2 hidden md:block">
            <img src="https://images.pexels.com/photos/7045356/pexels-photo-7045356.jpeg" alt="Cuisine équipée" className="w-full h-full object-cover opacity-70" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0b2960] via-transparent to-transparent" />
          </div>
          <div className="relative p-8 md:p-14 md:w-1/2">
            <div className="inline-block bg-[#e11d24] text-white text-xs font-bold px-3 py-1 rounded uppercase tracking-widest mb-3">Occasion reconditionnée</div>
            <h3 className="text-3xl md:text-4xl font-black text-white leading-tight">
              Des grandes marques à <span className="text-[#ff5a5f]">-70%</span> — testées, garanties 4 mois.
            </h3>
            <p className="mt-3 text-neutral-200 max-w-md">Stocks limités, renouvelés chaque semaine. Passez au magasin pour voir tous nos appareils.</p>
            <Link to="/occasion">
              <Button className="mt-5 bg-[#e11d24] hover:bg-[#b8171d] text-white h-11 px-6 font-bold">Voir toutes les occasions <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Nos produits NEUFS — section titre + sous-sections */}
      {produitsNeufs.length > 0 && (
        <section className="bg-gradient-to-b from-[#0b2960]/5 to-transparent py-6">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-end justify-between mb-2">
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-[#e11d24] flex items-center gap-1"><BadgeCheck className="h-3.5 w-3.5" /> Produits neufs</div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">Nos produits neufs</h2>
                <p className="text-sm text-neutral-500 mt-1">Grandes marques d'électroménager neuf, garantie constructeur.</p>
              </div>
              <Link to="/neuf" className="text-sm font-semibold text-[#e11d24] hover:underline">Tout voir →</Link>
            </div>
          </div>
        </section>
      )}

      {/* Réfrigérateurs neufs */}
      {neufsFrigo.length > 0 && (<section className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-green-700 flex items-center gap-1"><BadgeCheck className="h-3.5 w-3.5" /> Neuf</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">Réfrigérateurs neufs</h2>
          </div>
          <Link to="/neuf" className="text-sm font-semibold text-[#e11d24] hover:underline">Tout voir →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {neufsFrigo.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>)}

      {/* Lave-linge neufs */}
      {neufsLL.length > 0 && (<section className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-green-700 flex items-center gap-1"><BadgeCheck className="h-3.5 w-3.5" /> Neuf</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">Lave-linge neufs</h2>
          </div>
          <Link to="/neuf" className="text-sm font-semibold text-[#e11d24] hover:underline">Tout voir →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {neufsLL.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>)}

      {/* Sèche-linge neufs */}
      {neufsSL.length > 0 && (<section className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-green-700 flex items-center gap-1"><BadgeCheck className="h-3.5 w-3.5" /> Neuf</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">Sèche-linge neufs</h2>
          </div>
          <Link to="/neuf" className="text-sm font-semibold text-[#e11d24] hover:underline">Tout voir →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {neufsSL.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>)}

      {/* Autres neufs (TV, cuisson, lave-vaisselle, petit électro) */}
      {neufsAutres.length > 0 && (<section className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-green-700 flex items-center gap-1"><BadgeCheck className="h-3.5 w-3.5" /> Neuf</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">Cuisson, lave-vaisselle, TV & plus</h2>
          </div>
          <Link to="/neuf" className="text-sm font-semibold text-[#e11d24] hover:underline">Tout voir →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {neufsAutres.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>)}

      <BrandsBar />

      {/* Magasin section */}
      <section className="bg-white border-y border-neutral-200 py-14">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-10 items-center">
          <div className="relative">
            <div className="absolute -inset-3 bg-[#e11d24]/10 rounded-2xl -rotate-2" />
            <img src={STORE_INFO.storePhoto} alt="Magasin EDS ELECTROMENAGER Marseille" className="relative w-full rounded-xl shadow-xl object-cover aspect-[4/3]" />
            {/* Yellow business card overlay */}
            <div className="absolute -bottom-8 -right-6 md:-right-10 hidden sm:block">
              <BusinessCard rotate="rotate-6" />
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#e11d24]">Notre magasin</div>
            <h2 className="text-2xl md:text-4xl font-extrabold text-[#0b2960] mt-1 leading-tight">Venez nous rendre visite à Marseille</h2>
            <p className="mt-4 text-neutral-700 leading-relaxed">
              Situé au cœur du 10ᵉ arrondissement de Marseille, EDS ELECTROMENAGER est <b>spécialiste de l'électroménager d'occasion et neuf</b> : frigos, lave-linge, lave-vaisselle, fours, TV, hi-fi et petit électroménager. Nos produits d'occasion sont testés, révisés et garantis 4 mois.
            </p>
            <ul className="mt-5 space-y-2 text-sm">
              <li className="flex items-center gap-2 text-neutral-700"><LucideIcons.MapPin className="h-4 w-4 text-[#e11d24]" /> {STORE_INFO.address}</li>
              <li className="flex items-center gap-2 text-neutral-700"><LucideIcons.Phone className="h-4 w-4 text-[#e11d24]" /> {STORE_INFO.phone}</li>
              <li className="flex items-center gap-2 text-neutral-700"><LucideIcons.Clock className="h-4 w-4 text-[#e11d24]" /> Ouvert du Lundi au Samedi</li>
            </ul>
            <div className="mt-6 flex gap-3">
              <Link to="/magasin"><Button className="bg-[#0b2960] hover:bg-[#081e47] text-white h-11 px-5">Voir le magasin</Button></Link>
              <a href={`tel:${STORE_INFO.phone.replace(/\s/g, "")}`}><Button variant="outline" className="border-[#0b2960] text-[#0b2960] hover:bg-[#0b2960] hover:text-white h-11 px-5">Nous appeler</Button></a>
            </div>
          </div>
        </div>
      </section>

      {/* News */}
      <section className="max-w-7xl mx-auto px-4 py-14">
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-widest font-bold text-[#e11d24]">Actualités</div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">Nos derniers articles</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {NEWS.map((n) => (
            <article key={n.id} className="bg-white border border-neutral-200 rounded-xl overflow-hidden hover:shadow-lg transition-all group">
              <div className="aspect-video overflow-hidden">
                <img src={n.image} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5">
                <div className="text-xs text-neutral-500">{n.date}</div>
                <h3 className="font-bold text-[#0b2960] mt-1 leading-snug">{n.title}</h3>
                <p className="text-sm text-neutral-600 mt-2 line-clamp-2">{n.excerpt}</p>
                <div className="mt-3 text-sm font-semibold text-[#e11d24] group-hover:underline">Lire l'article →</div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
